import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { hashPassword } from '../utils/hash';

const COOKIE_NAME = 'sede_galatea_admin_hash';
const SUPER_ADMIN_COOKIE = 'superAdmin';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 anno

function readCookie(name: string): string | null {
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.slice(name.length + 1));
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? '; Secure'
      : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
}

export function getStoredAdminHash(): string | null {
  return readCookie(COOKIE_NAME);
}

export function setAdminAuthCookie(hash: string): void {
  writeCookie(COOKIE_NAME, hash, COOKIE_MAX_AGE_SECONDS);
}

export function logoutAdmin(): void {
  deleteCookie(COOKIE_NAME);
}

/** Cookie `superAdmin=true` (impostato a mano). Solo UI: abilita eliminazione richieste. */
export function isSuperAdmin(): boolean {
  return readCookie(SUPER_ADMIN_COOKIE) === 'true';
}

/** Documento Firestore: configurations/admin, campo hash-password */
export async function fetchAdminPasswordHash(): Promise<string> {
  const snap = await getDoc(doc(db, 'configurations', 'admin'));
  if (!snap.exists()) {
    throw new Error(
      'Configurazione admin mancante su Firebase (configurations/admin).'
    );
  }
  const hash = snap.data()?.['hash-password'];
  if (typeof hash !== 'string' || !hash.trim()) {
    throw new Error('Campo hash-password assente o non valido.');
  }
  return hash.trim().toLowerCase();
}

/** True se il cookie coincide con l’hash su Firebase. */
export async function isAdminAuthenticated(): Promise<boolean> {
  const stored = getStoredAdminHash();
  if (!stored) return false;
  try {
    const remote = await fetchAdminPasswordHash();
    return stored.toLowerCase() === remote;
  } catch {
    return false;
  }
}

/**
 * Verifica la password contro l’hash su Firebase.
 * In caso di successo salva l’hash in cookie (1 anno).
 */
export async function loginAdmin(password: string): Promise<boolean> {
  const remote = await fetchAdminPasswordHash();
  const local = await hashPassword(password);
  if (local === remote) {
    setAdminAuthCookie(local);
    return true;
  }
  return false;
}
